import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { forwardRef, useEffect, useRef, useState } from "react";

export const TextInputCell = ({
  value,
  tooltip,
  saveHandler,
  className,
  inputKey,
  readOnly,
  disabled,
  allowedCharsRegex,
}: {
  value: any;
  tooltip?: string;
  inputKey: string;
  disabled?: boolean | string | React.ReactNode;
  saveHandler: (value: string) => Promise<any>;
  className?: string;
  allowedCharsRegex?: RegExp;
  readOnly?: boolean | string | React.ReactNode;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(value);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const checkEllipsis = () => {
      if (ref.current) {
        setIsEllipsisActive(ref.current.scrollWidth > ref.current.clientWidth);
      }
    };

    checkEllipsis();

    window.addEventListener("resize", checkEllipsis);
    return () => {
      window.removeEventListener("resize", checkEllipsis);
    };
  }, [value]);

  useEffect(() => {
    setText(value);
  }, [value]);

  const onChange = (value: string) => {
    setText(value);
  };

  const onBlur = async () => {
    await saveHandler(text);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          className={cn("overflow-hidden text-ellipsis py-0.5 w-full px-0.5")}
        >
          <TableTextInput
            ref={ref}
            className={className}
            inputKey={inputKey}
            disabled={disabled}
            value={text}
            onFocus={onFocus}
            onChange={onChange}
            onBlur={onBlur}
            readOnly={readOnly}
            allowedCharsRegex={allowedCharsRegex}
          />
        </TooltipTrigger>
        {isEllipsisActive && <TooltipContent>{tooltip}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
};

const TableTextInput = forwardRef<
  HTMLInputElement,
  {
    inputKey: string;
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    className?: string;
    disabled?: boolean | string | React.ReactNode;
    readOnly?: boolean | string | React.ReactNode;
    allowedCharsRegex?: RegExp;
  }
>(
  (
    {
      inputKey,
      value,
      onChange,
      onBlur,
      onFocus,
      className,
      disabled,
      readOnly,
      allowedCharsRegex,
    },
    ref,
  ) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!allowedCharsRegex || allowedCharsRegex.test(newValue)) {
        onChange(newValue);
      }
    };

    return (
      <Input
        ref={ref}
        key={inputKey}
        type="text"
        className={cn(
          "text-left border-0 font-normal text-sm rounded bg-transparent overflow-hidden text-ellipsis px-2 focus:outline-muted-foreground focus:outline focus:border-input",
          className,
        )}
        value={value}
        readOnly={readOnly}
        onChange={handleInputChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
      />
    );
  },
);

// in case we would like to add hover effect
// hover:outline-muted-foreground hover:outline hover:border-input
